import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { transformColor, type ColorTransformOptions } from '../utils/colorTransform';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import { ThemeCustomizerButton, SiteThemeCustomizer } from '../components/desktop/ThemeCustomizer/index';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { Bell, Heart, MessageSquare, Edit3, Users, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import MainNavbar from '../components/desktop/MainNavbar';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../utils/postUrls';
import { notificationEventBus } from '../lib/notificationEventBus';
import { useNotificationSync } from '../hooks/useNotificationSync';
import { calculateNavbarBg } from '../utils/backgroundHelpers';
import { getCachedTheme } from '../utils/themeCache';

import { fetchWithAuth } from '../lib/fetchWithAuth';
export default function Marketplace() {
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { user: currentUser, loading: authLoading, logout, setShowLoginModal } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [customization, setCustomization] = useState<any>(siteCustomization || {
    textColor: theme.textColor,
    accentColor: theme.accentColor,
    primaryColor: theme.primaryColor,
    secondaryColor: theme.secondaryColor,
    cardBg: theme.cardBg,
    navbarBg: theme.navbarBg,
    tabBarBg: theme.tabBarBg,
    colorSpace: 'Off',
    temperatureLimit: 50,
    magentaLimit: 50,
    intensityLimit: 50,
    backgroundSaturationLimit: 50,
    backgroundBrightnessLimit: 50,
    redLimit: 50,
    greenLimit: 50,
    blueLimit: 50,
    backgroundContrast: 50,
    backgroundHueGravity: 0,
    backgroundGraySaturation: 0,
    backgroundDefaultHue: 184,
    navbarOpacity: 92,
    tabBarOpacity: 88,
  });
  const [showSiteCustomizer, setShowSiteCustomizer] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Watch for siteCustomization updates from ThemeContext
  useEffect(() => {
    if (siteCustomization) {
            setCustomization(siteCustomization);
    }
  }, [siteCustomization]);

  // Create local theme with transformed colors for inline styles
  // Simply use theme from ThemeContext - it already has computedColors (transformed)
  // CRITICAL: Don't re-transform! Theme from ThemeContext is already correct
  const localTheme = React.useMemo(() => {
    // Theme from ThemeContext always has all required properties defined
    // from base presets - just return it directly
    return theme;
  }, [theme]);

  // Calculate navbarBg and tabBarBg using hex + opacity pattern
  const themeColors = React.useMemo(() => {
    // Use customization.navbarBg if set, otherwise use primaryColor
    const navbarBaseColor = customization.navbarBg || localTheme.primaryColor;
    const tabBarBaseColor = customization.tabBarBg || localTheme.primaryColor;

    return {
      navbarBg: calculateNavbarBg(navbarBaseColor, customization.navbarOpacity ?? 92),
      accentColor: localTheme.accentColor,
      textColor: localTheme.textColor,
      cardBg: localTheme.cardBg,
      tabBarBg: calculateNavbarBg(tabBarBaseColor, customization.tabBarOpacity ?? 88),
    };
  }, [localTheme, customization.navbarBg, customization.tabBarBg, customization.navbarOpacity, customization.tabBarOpacity]);

  // Sync animations when siteAnimations or currentPreset changes
  useEffect(() => {
    if (siteAnimations) {
      setThemeAnimations(siteAnimations);
    } else {
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [siteAnimations, currentPreset]);

  // Register site theme with NavigationContext - ONLY when theme fully loads
  // CRITICAL: Do NOT register on mount, or it will overwrite the previous page's theme
  // before LoadingScreen can read it
  useEffect(() => {
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      // Delay registration by 100ms to ensure LoadingScreen reads the previous page's theme
      const timer = setTimeout(() => {
        console.log('[Marketplace] 🎨 Setting currentTheme for loading screen (delayed):');
        setCurrentTheme({
          type: 'site',
          colors: {
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor || theme.primaryColor,
            textColor: theme.textColor || '#ffffff',
            accentColor: theme.accentColor || theme.primaryColor
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);


  // Site theme is already loaded by ThemeContext

  // Listen for theme updates from customizer for animations and customization
  useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      const { customization, animations, animationsEnabled: enabled } = event.detail;

      if (customization) {
        setCustomization(customization);
      }

      // Update animations enabled state
      if (typeof enabled === 'boolean') {
        setAnimationsEnabled(enabled);
      }

      // Fix and validate animations object structure before updating
      if (animations) {
        const fixedAnimations = { ...animations };

        // Fix incomplete bubbles structure
        if (fixedAnimations.bubbles && typeof fixedAnimations.bubbles === 'object') {
          if (!fixedAnimations.bubbles.id || !fixedAnimations.bubbles.type || !fixedAnimations.bubbles.count) {
            // If bubbles is incomplete, create proper structure based on enabled state
            fixedAnimations.bubbles = fixedAnimations.bubbles.enabled
              ? {
                  id: 'bubbles',
                  type: 'rising',
                  enabled: true,
                  count: 25,
                  speed: 0.8,
                  size: { min: 15, max: 35 },
                }
              : null;
          }
        }

        // Basic structure validation
        const hasValidStructure =
          fixedAnimations.characters && Array.isArray(fixedAnimations.characters) &&
          fixedAnimations.decorations && Array.isArray(fixedAnimations.decorations) &&
          fixedAnimations.characters.length > 0 &&
          fixedAnimations.characters[0]?.id;

        if (hasValidStructure) {
          setThemeAnimations(fixedAnimations);
        }
      }
    };

    window.addEventListener('siteThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('siteThemeUpdated', handleThemeUpdate);
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  const navItems = [
    { label: 'Home', path: '/feed' },
    { label: 'Friends', path: '/friends' },
    { label: 'Messages', path: '/messages' },
    { label: 'Community', path: '/community' },
    { label: 'Ads', path: '/ads' },
    { label: 'Marketplace', path: '/marketplace' }
  ];

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        // Use actorUsername instead of actorId
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`); // Fallback
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        // Route based on postType and slug
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed'); // Fallback
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  };

  const handleLogout = () => {
    logout();
  };

  const PixelatedTurtle = ({ size = 32 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" className="pixelated">
      <g fill="#10b981">
        <rect x="12" y="8" width="8" height="8" />
        <rect x="10" y="10" width="2" height="4" />
        <rect x="20" y="10" width="2" height="4" />
        <rect x="8" y="14" width="4" height="4" />
        <rect x="20" y="14" width="4" height="4" />
        <rect x="6" y="12" width="2" height="2" />
        <rect x="24" y="12" width="2" height="2" />
        <rect x="10" y="18" width="2" height="2" />
        <rect x="20" y="18" width="2" height="2" />
        <rect x="14" y="6" width="4" height="2" />
      </g>
    </svg>
  );

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden" style={{ background: theme.appBg, color: localTheme.textColor }}>
      <style>{`
        .pixelated {
          image-rendering: pixelated;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
        }
      `}</style>

      <AnimationLayer
        animations={themeAnimations}
        isMobile={false}
        bubbleColor={localTheme.accentColor}
        enabled={animationsEnabled}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        key={`navbar-${themeColors.navbarBg}`}
        currentPath="/marketplace"
        onLogout={handleLogout}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
        onSignIn={() => setShowLoginModal(true)}
      />

      {/* Page Content */}
      <div className="flex flex-col items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <h1 className="text-5xl font-bold text-center px-6" style={{ color: localTheme.textColor }}>
          More Features like Widgets and Trading Coming Soon!
        </h1>
      </div>

      {/* Standardized Theme Customizer Button */}
      <ThemeCustomizerButton
        onClick={() => setShowSiteCustomizer(!showSiteCustomizer)}
        tooltipText="Customize Site Theme"
        accentColor={localTheme.accentColor}
        textColor={localTheme.textColor}
        show={true}
      />

      {/* Site Theme Customizer Modal */}
      <SiteThemeCustomizer
        isOpen={showSiteCustomizer}
        onClose={() => setShowSiteCustomizer(false)}
        userId={currentUser?.id}
        initialCustomization={customization}
        initialAnimations={themeAnimations}
      />
    </div>
  );
}
